import App, {
    PageContext,
    LocalStorage,
    initCallTracking,
    renderReplacement,
    error,
    info,
    isDebugging,
    PageViewEvent,
    debug,
    undoReplacements,
} from './lib';

import {initAscDataLayerMonitoring} from './lib/asc-data-layer';
import {initEventStorage, initEventTracker} from './lib/event-tracker';
import {initGoogleAnalytics} from './lib/ga';
import {initHeartbeat} from './lib/heartbeat';
import {Api} from "./lib/repositories/remote";

declare global {
    interface Window {
        _800js: {
            debug?: boolean
            ttl?: number
        };

        _800testing?: {
            url?: string,
            referrer?: string,
            title?: string,
        };

        _800swaps: string[],
    }
}

window._800js ||= {};
window._800testing ||= {};
window._800swaps ||= [];

const getPageContext = (): PageContext => {
    return {
        url: window._800testing?.url || window.location.href,
        referrer: window._800testing?.referrer || document.referrer,
        title: window._800testing?.title || document.title,
    };
};

const script = document.currentScript;

const current = script
    ? new URL((script as HTMLScriptElement).src)
    : null;

const token = current?.searchParams.get('token') || script?.getAttribute('data-token') || String(process.env.PUBLIC_API_TOKEN);
const url =  (current?.searchParams.get('backend') || script?.getAttribute('data-backend') || String(process.env.PUBLIC_API_ENDPOINT))
    // remote trailing slash because we need to build different URL paths
    .replace(/\/$/, '');

if (!url) {
    error('missing url');
}

if (url) {
    const storage = new LocalStorage(window._800js.ttl || 3600);
    const pageContext = getPageContext();
    const api = new Api(`${url}/calltrk/${token}`);

    const callTracking = initCallTracking(api, {
        storage,
        apiUrl: `${url}/calltrk/${token}`,
        pageContext: pageContext,
    });

    info('booting', window._800js);

    // to check that we are swapping with selenium testing, must be set before the app is run
    // or we'll swap before we assign the listener.
    window.addEventListener('800dni.swap', function (e) {
        if (!window._800swaps.includes(e.detail.replacementE164)) {
            window._800swaps.push(e.detail.replacementE164);
        }
    });

    const eventStorage = initEventStorage();

    const app = App.getInstance(
        callTracking,
        renderReplacement,
        undoReplacements,
        storage,
        api,
    )
        .run();

    debug('started app');

    if (isDebugging()) {
        Object.assign(window._800js, {app});
    }

    (async () => {
        const swapResponse = await callTracking.pending;

        if (!swapResponse?.sessionId || 
            !swapResponse?.isSessionTrackingEnabled) {
            return;
        }
        
        const sessionId = swapResponse?.sessionId;

        debug({sessionId})

        initEventTracker(eventStorage, url, token, sessionId);
        initHeartbeat(url, token, sessionId);
        initGoogleAnalytics();
        initAscDataLayerMonitoring();

        window.dispatchEvent(
            new CustomEvent<PageViewEvent>('800dni.page_view', {
                detail: {
                    url: pageContext.url,
                    referrer: pageContext.referrer || null,
                    title: pageContext.title,
                },
            })
        )
    })();
}

